/**
 * Notificacoes do cliente (spec 038, secao 3): todas ou so as nao lidas, 20 por vez ("Mostrar mais" pede as anteriores
 * a ultima da tela, pelo `antes_de`), abrir leva ao link e marca como lida, e da pra marcar uma ou todas como lidas.
 * O sino e o Inicio mostram so as ultimas; o "Ver todas" deles chega aqui.
 */
import { Abas, Aviso, Botao, CabecalhoDePagina, Carregando, EsqueletoDeCartao, ListaDeNotificacoes, Pilha, Vazio, mensagemDe, type Notificacao } from "@mtc/ui";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useApi } from "../acoes";
import type { ListaDeAvisos } from "../tipos";

const POR_VEZ = 20;
type Filtro = "todas" | "nao_lidas";

export function PaginaNotificacoes() {
  const { ler, postar } = useApi();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [filtro, setFiltro] = useState<Filtro>("todas");
  const [erro, setErro] = useState<string>();
  const q = useInfiniteQuery({
    queryKey: ["avisos", "pagina", filtro],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => ler<ListaDeAvisos>(`/v1/me/notificacoes?limite=${POR_VEZ}${filtro === "nao_lidas" ? "&nao_lidas=true" : ""}${pageParam ? `&antes_de=${encodeURIComponent(pageParam)}` : ""}`),
    // Pagina cheia: pode haver mais antes da ultima. Menos que o limite: acabou.
    getNextPageParam: (ultima) => (ultima.itens.length === POR_VEZ ? ultima.itens.at(-1)!.criada_em : null),
  });
  const itens = q.data?.pages.flatMap((p) => p.itens) ?? [];
  const naoLidas = q.data?.pages[0]?.nao_lidas ?? 0;
  // O sino, o Inicio e esta pagina leem da mesma fonte: marcou aqui, os tres atualizam.
  const recarregar = () => qc.invalidateQueries({ queryKey: ["avisos"] });
  const marcar = async (n: Notificacao) => {
    setErro(undefined);
    try {
      await postar(`/v1/me/notificacoes/${n.id}/lida`);
      await recarregar();
    } catch (e) {
      setErro(mensagemDe(e, "Não deu pra marcar agora. Tente de novo em instantes."));
    }
  };
  const abrir = (n: Notificacao) => {
    if (!n.lida) void postar(`/v1/me/notificacoes/${n.id}/lida`).then(recarregar, () => undefined);
    if (n.link?.startsWith("/")) navegar(n.link);
  };
  const [marcandoTodas, setMarcandoTodas] = useState(false);
  const marcarTodas = async () => {
    setMarcandoTodas(true);
    setErro(undefined);
    try {
      await postar("/v1/me/notificacoes/lidas");
      await recarregar();
    } catch (e) {
      setErro(mensagemDe(e, "Não deu pra marcar agora. Tente de novo em instantes."));
    } finally {
      setMarcandoTodas(false);
    }
  };
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina
        titulo="Notificações"
        descricao="Os avisos da equipe e do sistema sobre seus planos, pagamentos e a Academy."
        acoes={naoLidas > 0 ? <Botao variante="secundario" tamanho="pequeno" carregando={marcandoTodas} onClick={() => void marcarTodas()}>Marcar todas como lidas</Botao> : undefined}
        abas={<Abas rotulo="Quais notificações" valor={filtro} aoMudar={setFiltro} abas={[{ valor: "todas", rotulo: "Todas" }, { valor: "nao_lidas", rotulo: "Não lidas", contagem: naoLidas }]} />}
      />
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {q.isLoading ? (
        <EsqueletoDeCartao linhas={3} rotulo="Buscando suas notificações" />
      ) : q.isError ? (
        <Aviso tipo="erro">{mensagemDe(q.error, "Não conseguimos carregar suas notificações agora. Recarregue a página.")}</Aviso>
      ) : !itens.length ? (
        <Vazio titulo={filtro === "nao_lidas" ? "Nada por ler" : "Nenhuma notificação ainda"}>{filtro === "nao_lidas" ? "Você já leu todas as notificações." : "Quando a equipe ou o sistema tiver um aviso pra você, ele aparece aqui e no sino."}</Vazio>
      ) : (
        <div className="mtc-cartao" style={{ padding: "var(--e-2)" }}>
          <ListaDeNotificacoes itens={itens} aoAbrir={abrir} aoMarcarLida={(n) => void marcar(n)} />
        </div>
      )}
      {q.hasNextPage && (
        <div>
          {q.isFetchingNextPage ? <Carregando compacto texto="Buscando as anteriores" /> : <Botao variante="secundario" onClick={() => void q.fetchNextPage()}>Mostrar mais</Botao>}
        </div>
      )}
    </Pilha>
  );
}
